import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, Phone, Globe, Clock, ArrowLeft, Mail, Navigation, Tag, Briefcase } from "lucide-react";
import { api, fileUrl } from "../lib/api";
import { ListingHeroImg } from "../lib/termImages";
import { PipeText } from "../lib/pipeText";
import { MerchGrid } from "../components/VenueExtras";
import { AttractionCard, ActivityCard } from "../components/Cards";
import { Reveal, SectionTitle, useSeo } from "../components/VIP";
import { ListingActions } from "../components/ListingActions";
import { Paged } from "../components/PagedList";

const MAP = (q) => `https://maps.google.com/maps?q=${encodeURIComponent(q)}&t=&z=13&ie=UTF8&iwloc=&output=embed`;

const Info = ({ Icon, label, children }) => (
  <div className="flex items-start gap-3">
    <Icon size={16} className="text-rose-400 mt-0.5 shrink-0" />
    <div><p className="text-xs text-zinc-500 uppercase tracking-wider">{label}</p><div className="text-sm text-zinc-200 break-words">{children}</div></div>
  </div>
);

export default function BusinessDetail() {
  const { slug } = useParams();
  const [b, setB] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get(`/businesses/${slug}`).then((r) => setB(r.data)).catch(() => setErr(true)); window.scrollTo(0, 0); }, [slug]);
  useSeo(b ? (b.seo_title || `${b.name} | Gulf Coast VIP`) : "Business", b?.seo_description || b?.short_description);

  if (err) return <div className="py-32 text-center text-zinc-500">Business not found. <Link to="/businesses" className="text-rose-400">Browse businesses</Link></div>;
  if (!b) return <div className="py-32 text-center text-zinc-600 animate-pulse">Loading…</div>;

  const hasContact = b.phone || b.email || b.website || b.address || b.hours;

  return (
    <div data-testid="business-detail">
      <section className="relative min-h-[46vh] flex flex-col justify-end overflow-hidden">
        <ListingHeroImg ent={b} kind="business" className="absolute inset-0 w-full h-full object-cover hero-zoom" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/20" />
        <div className="relative px-6 md:px-12 lg:px-20 pb-8 pt-28">
          <Link to="/businesses" className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white mb-3"><ArrowLeft size={14} /> All businesses</Link>
          <div className="flex flex-wrap items-center gap-2 mb-2">
            {b.business_type && <span className="bg-rose-500 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">{b.business_type}</span>}
          </div>
          <div className="flex items-center gap-4">
            {b.logo && <img src={fileUrl(b.logo)} alt="" className="w-16 h-16 rounded-2xl object-cover border border-white/15 shrink-0" data-testid="business-logo" />}
            <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl">{b.name}</h1>
          </div>
          {(b.city || b.state) && <p className="text-zinc-300 mt-2 flex items-center gap-1.5"><MapPin size={15} /> {[b.address, b.city, b.state].filter(Boolean).join(", ")}</p>}
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <ListingActions entityType="business" entityId={b.id} claimStatus={b.claim_status || "unclaimed"} verified={!!b.verified} />
          </div>
        </div>
      </section>

      <div className="px-6 md:px-12 lg:px-20 py-14 grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 space-y-16">
          {(b.description || b.short_description) && (
            <Reveal>
              <SectionTitle kicker="About" title="Overview" />
              <PipeText text={b.description || b.short_description} className="text-zinc-300 leading-relaxed mt-6 text-lg" />
            </Reveal>
          )}

          {b.attractions?.length > 0 && (
            <Reveal>
              <SectionTitle kicker="Operated by this business" title="Attractions" />
              <Paged items={b.attractions} perKey="business_attractions_pp" perDefault={6} label="attractions" testid="business-attractions-paged" render={(slice) => (
                <div className="grid sm:grid-cols-2 gap-5 mt-6" data-testid="business-attractions">
                  {slice.map((a) => <AttractionCard key={a.id} a={a} />)}
                </div>
              )} />
            </Reveal>
          )}

          {b.activities?.length > 0 && (
            <Reveal>
              <SectionTitle kicker="Operated by this business" title="Activities & Tours" />
              <Paged items={b.activities} perKey="business_activities_pp" perDefault={6} label="activities" testid="business-activities-paged" render={(slice) => (
                <div className="grid sm:grid-cols-2 gap-5 mt-6" data-testid="business-activities">
                  {slice.map((a) => <ActivityCard key={a.id} a={a} />)}
                </div>
              )} />
            </Reveal>
          )}

          {b.deals?.length > 0 && (
            <Reveal className="lg:hidden">
              <SectionTitle kicker="Save money" title="Current Deals" />
              <Paged items={b.deals} perKey="business_deals_pp" perDefault={4} label="deals" testid="business-deals-paged" render={(slice) => (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6" data-testid="business-deals">
                  {slice.map((d) => (
                    <Link key={d.id} to={`/deals/${d.slug || d.id}`} className="bg-[#111] border border-white/10 rounded-2xl p-5 card-hover block" data-testid={`business-deal-${d.slug || d.id}`}>
                      <p className="overline !text-[10px] mb-1 flex items-center gap-1"><Tag size={11} /> {d.category || "Deal"}</p>
                      <h4 className="font-display font-bold text-lg">{d.title}</h4>
                      {d.short_description && <p className="text-sm text-zinc-400 mt-1">{d.short_description}</p>}
                      {d.discounted_price && <p className="text-rose-400 font-bold mt-2">${d.discounted_price}{d.original_price ? <span className="text-zinc-500 line-through ml-2 font-normal">${d.original_price}</span> : null}</p>}
                    </Link>
                  ))}
                </div>
              )} />
            </Reveal>
          )}

          {b.jobs?.length > 0 && (
            <Reveal className="lg:hidden">
              <SectionTitle kicker="Join the team" title="Open Positions" />
              <Paged items={b.jobs} perKey="business_jobs_pp" perDefault={4} label="jobs" testid="business-jobs-paged" render={(slice) => (
                <div className="grid sm:grid-cols-2 gap-4 mt-6" data-testid="business-jobs">
                  {slice.map((j) => (
                    <Link key={j.id} to={`/hiring/${j.slug}`} className="bg-[#111] border border-white/10 rounded-2xl p-5 card-hover block" data-testid={`business-job-${j.slug}`}>
                      <p className="overline !text-[10px] mb-1 flex items-center gap-1"><Briefcase size={11} /> {j.job_category || "Job"}</p>
                      <h4 className="font-display font-bold text-lg">{j.title}</h4>
                      <p className="text-sm text-zinc-400 mt-1">{[j.job_type, j.city, j.pay_range].filter(Boolean).join(" · ")}</p>
                    </Link>
                  ))}
                </div>
              )} />
            </Reveal>
          )}

          {((b.merch?.length || 0) + (b.marketplace_merch?.length || 0)) > 0 && (
            <Reveal>
              <SectionTitle kicker="Shop" title="Merch & Products" />
              <Paged items={[...(b.marketplace_merch || []), ...(b.merch || [])]} perKey="business_merch_pp" perDefault={6} label="items" testid="business-merch-paged" render={(slice) => <MerchGrid items={slice} title="Products & Extras" />} />
            </Reveal>
          )}
        </div>

        <aside className="space-y-6">
          {/* 1. Contact & Info */}
          {hasContact && (
            <div className="bg-[#111] border border-white/10 rounded-2xl p-6 space-y-5" data-testid="business-contact-panel">
              <p className="overline">Contact & Info</p>
              {b.address && <Info Icon={MapPin} label="Address">{[b.address, b.city, b.state, b.zip].filter(Boolean).join(", ")}</Info>}
              {b.phone && <Info Icon={Phone} label="Phone"><a href={`tel:${b.phone}`} className="hover:text-rose-400">{b.phone}</a></Info>}
              {b.email && <Info Icon={Mail} label="Email"><a href={`mailto:${b.email}`} className="hover:text-rose-400">{b.email}</a></Info>}
              {b.website && <Info Icon={Globe} label="Website"><a href={b.website} target="_blank" rel="noreferrer" className="hover:text-rose-400">{b.website.replace(/^https?:\/\//, "")}</a></Info>}
              {b.hours && <Info Icon={Clock} label="Hours"><PipeText text={b.hours} /></Info>}
            </div>
          )}

          {/* 2. Gallery */}
          {b.gallery_images?.length > 0 && (
            <div className="bg-[#111] border border-white/10 rounded-2xl p-6" data-testid="business-side-gallery">
              <h3 className="font-display font-bold text-lg mb-4">Gallery</h3>
              <Paged items={b.gallery_images} perKey="business_gallery_pp" perDefault={4} label="photos" testid="business-gallery-paged" render={(slice) => (
                <div className="grid grid-cols-2 gap-2">
                  {slice.map((g, i) => <img key={i} src={fileUrl(g)} alt="" loading="lazy" className="w-full aspect-square object-cover rounded-xl" />)}
                </div>
              )} />
            </div>
          )}

          {/* 3. Current Deals */}
          {b.deals?.length > 0 && (
            <div className="hidden lg:block bg-[#111] border border-white/10 rounded-2xl p-6" data-testid="business-side-deals">
              <div className="flex items-baseline justify-between mb-4">
                <h3 className="font-display font-bold text-lg">Current Deals</h3>
                <Link to="/deals" className="text-xs font-bold text-rose-400 hover:text-rose-300" data-testid="business-side-deals-all">View All</Link>
              </div>
              <Paged items={b.deals} perKey="business_side_deals_pp" perDefault={3} label="deals" testid="business-side-deals-paged" render={(slice) => (
                <ul className="space-y-3">
                  {slice.map((d) => (
                    <li key={d.id}>
                      <Link to={`/deals/${d.slug || d.id}`} className="block group bg-white/[0.02] hover:bg-white/[0.05] border border-white/5 hover:border-white/15 rounded-xl p-3 transition-colors" data-testid={`business-side-deal-${d.slug || d.id}`}>
                        <p className="text-[10px] uppercase tracking-wider text-rose-300 flex items-center gap-1"><Tag size={10} /> {d.category || "Deal"}</p>
                        <p className="font-semibold text-sm text-white group-hover:text-rose-300 leading-snug mt-0.5 line-clamp-2">{d.title}</p>
                        {d.discounted_price && (
                          <p className="mt-1.5"><span className="font-display font-bold text-rose-400">${d.discounted_price}</span>{d.original_price && <span className="text-zinc-500 line-through text-xs ml-2">${d.original_price}</span>}</p>
                        )}
                      </Link>
                    </li>
                  ))}
                </ul>
              )} />
            </div>
          )}

          {/* 4. Open Positions */}
          {b.jobs?.length > 0 && (
            <div className="hidden lg:block bg-[#111] border border-white/10 rounded-2xl p-6" data-testid="business-side-jobs">
              <div className="flex items-baseline justify-between mb-4">
                <h3 className="font-display font-bold text-lg">Open Positions</h3>
                <Link to="/hiring" className="text-xs font-bold text-rose-400 hover:text-rose-300" data-testid="business-side-jobs-all">View All</Link>
              </div>
              <Paged items={b.jobs} perKey="business_side_jobs_pp" perDefault={3} label="jobs" testid="business-side-jobs-paged" render={(slice) => (
                <ul className="space-y-3">
                  {slice.map((j) => (
                    <li key={j.id}>
                      <Link to={`/hiring/${j.slug}`} className="block group" data-testid={`business-side-job-${j.slug}`}>
                        <p className="text-[10px] uppercase tracking-wider text-rose-300 flex items-center gap-1"><Briefcase size={10} /> {j.job_category || "Job"}</p>
                        <p className="font-semibold text-sm text-white group-hover:text-rose-300 leading-snug mt-0.5">{j.title}</p>
                        {(j.job_type || j.pay_range) && <p className="text-xs text-zinc-500 mt-0.5">{[j.job_type, j.pay_range].filter(Boolean).join(" · ")}</p>}
                      </Link>
                    </li>
                  ))}
                </ul>
              )} />
            </div>
          )}

          {/* 5. Map & Directions */}
          {(b.address || b.city || b.map_link) && (
            <div className="hidden lg:block bg-[#111] border border-white/10 rounded-2xl p-6" data-testid="business-side-map">
              <h3 className="font-display font-bold text-lg mb-4">Map & Directions</h3>
              <div className="rounded-xl overflow-hidden border border-white/10">
                <iframe title="map" src={MAP([b.address, b.city, b.state].filter(Boolean).join(", ") || `${b.city}, ${b.state}`)} className="w-full h-56 grayscale invert-[.9]" loading="lazy" />
              </div>
              <a href={b.map_link || `https://maps.google.com/?q=${encodeURIComponent([b.address, b.city, b.state].filter(Boolean).join(", "))}`}
                target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-rose-300 hover:text-rose-200" data-testid="business-side-directions"><Navigation size={12} /> Get directions</a>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
